"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";

import type { IDoctor } from "@/app/interfaces/doctor.interface";
import type IHeaderSchema from "@/app/interfaces/header";
import TimeSlotSelector from "@/app/components/TimeSlotSelector";

interface DoctorSelectorProps {
  locale: string;
  headers: IHeaderSchema;
  onAppointmentComplete: (doctorId: string, timeSlotId: string, date: string, time: string) => void;
}

const t = (key: string, headers: IHeaderSchema): string => {
  return (headers as unknown as Record<string, string>)[key] || key;
};

export default function DoctorSelector({ locale, headers, onAppointmentComplete }: DoctorSelectorProps) {
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<IDoctor | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/api/doctors", {
          params: { locale },
        });
        setDoctors(response.data || []);
        if (response.data && response.data.length > 0) {
          setSelectedDoctor(response.data[0]);
        }
      } catch (err) {
        setError(t("noDoctorsAvailable", headers));
        console.error("Error fetching doctors:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [locale, headers]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t("loading", headers)}</p>
      </div>
    );
  }

  if (error || doctors.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error || t("noDoctorsAvailable", headers)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Doctor Selector */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          {t("selectDoctor", headers)}
        </label>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full bg-white border-2 border-gray-300 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
          >
            <div className="flex-1">
              <p className="font-semibold text-gray-800">{selectedDoctor?.name}</p>
              <p className="text-sm text-gray-600">{selectedDoctor?.specialty}</p>
            </div>
            <ChevronDownIcon
              className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
            />
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-lg z-10">
              {doctors.map((doctor) => (
                <button
                  key={doctor.id}
                  onClick={() => {
                    setSelectedDoctor(doctor);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between transition-colors"
                >
                  <div>
                    <p className="font-semibold text-gray-800">{doctor.name}</p>
                    <p className="text-sm text-gray-600">{doctor.specialty}</p>
                  </div>
                  {selectedDoctor?.id === doctor.id && (
                    <CheckIcon className="w-5 h-5 text-blue-600" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Time Slot Selector */}
      {selectedDoctor && (
        <TimeSlotSelector
          locale={locale}
          headers={headers}
          doctorId={selectedDoctor.id}
          doctorName={selectedDoctor.name}
          onAppointmentComplete={onAppointmentComplete}
        />
      )}
    </div>
  );
}
